import { useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { UserButton } from "@clerk/clerk-react";
import {
  LayoutDashboard,
  History,
  Menu,
  X,
  FileText,
  Users,
  Receipt,
  MessageCircle,
  UserCheck,
  ClipboardList,
  Bike,
  ExternalLink,
  Inbox,
  ScrollText,
  PanelLeftClose,
  PanelLeftOpen,
  Link2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const navItems = [
  { type: "divider", label: "General" },
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { type: "divider", label: "Operations" },
  { path: "/orders", label: "Active Orders", icon: ClipboardList },
  { path: "/payee-queue", label: "Payee Queue", icon: Inbox },
  { path: "/rider-logs", label: "Rider Trip Logs", icon: ScrollText },
  { type: "external", href: "/rider", label: "Rider App", icon: Bike },
  { type: "divider", label: "Invoices" },
  { path: "/vendor-invoice", label: "Invoice", icon: Receipt },
  { path: "/history", label: "Customer Invoice History", icon: History },
  { path: "/vendor-history", label: "Vendor Invoice History", icon: History },
  { type: "divider", label: "Masters" },
  { path: "/vendors", label: "Vendor Master", icon: Users },
  { path: "/staff", label: "Staff Master", icon: UserCheck },
  { path: "/riders", label: "Rider Master", icon: Bike },
  { type: "divider", label: "Integrations" },
  { path: "/chatwoot", label: "Chatwoot", icon: MessageCircle },
  { path: "/razorpay", label: "Razorpay Links", icon: Link2 },
];

export const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile header bar */}
      <div className="fixed top-0 left-0 right-0 z-30 h-14 bg-white border-b border-slate-200 flex items-center px-4 lg:hidden">
        <Button
          variant="ghost"
          size="icon"
          className="mr-3 h-9 w-9"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-7 h-7 rounded-md bg-[#0047FF]">
            <FileText className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="font-bold text-slate-900 text-sm">Pokus Invoices</span>
        </div>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed left-0 top-0 z-50 h-screen w-64 bg-white border-r border-slate-200 transition-transform duration-300",
          sidebarCollapsed ? "lg:-translate-x-full" : "lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 py-6 border-b border-slate-200">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#0047FF]">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900 tracking-tight">Pokus</h1>
              <p className="text-xs text-slate-500">Service Ops</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
            {navItems.map((item, index) => {
              if (item.type === "divider") {
                const isFirst = index === 0;
                return (
                  <div key={index} className={cn("pb-1", isFirst ? "pt-0" : "pt-4")}>
                    <p className="px-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                      {item.label}
                    </p>
                  </div>
                );
              }

              if (item.type === "external") {
                const Icon = item.icon;
                return (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setSidebarOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-all duration-150"
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                    <ExternalLink className="w-3 h-3 ml-auto text-slate-400" />
                  </a>
                );
              }

              const Icon = item.icon;
              const isActive = location.pathname === item.path ||
                (item.path !== "/" && location.pathname.startsWith(item.path));
              const isVendorItem = item.path.startsWith("/vendor");
              const isOpsItem = item.path.startsWith("/orders") || item.path.startsWith("/riders") || item.path.startsWith("/rider-logs") || item.path.startsWith("/payee");

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.path.replace(/\//g, "") || "dashboard"}`}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150",
                    isActive
                      ? isOpsItem
                        ? "bg-orange-600 text-white shadow-sm"
                        : isVendorItem
                          ? "bg-emerald-600 text-white shadow-sm"
                          : "bg-[#0047FF] text-white shadow-sm"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  )}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="truncate">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Collapse toggle (desktop only) */}
          <div className="hidden lg:block px-3 py-2 border-t border-slate-200">
            <button
              onClick={() => setSidebarCollapsed(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium text-slate-400 hover:text-slate-600 hover:bg-slate-50 w-full transition-colors"
            >
              <PanelLeftClose className="w-4 h-4" />
              <span>Collapse</span>
            </button>
          </div>

          {/* Footer */}
          <div className="flex items-center gap-3 px-6 py-4 border-t border-slate-200">
            <UserButton afterSignOutUrl="/" />
            <p className="text-xs text-slate-400">
              Pokus Technologies Pvt Ltd
            </p>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={cn("min-h-screen pt-14 lg:pt-0 transition-[margin] duration-300", sidebarCollapsed ? "lg:ml-0" : "lg:ml-64")}>
        {/* Expand sidebar button (desktop, when collapsed) */}
        {sidebarCollapsed && (
          <button
            onClick={() => setSidebarCollapsed(false)}
            className="hidden lg:flex fixed left-3 top-3 z-40 items-center justify-center w-9 h-9 rounded-lg bg-white border border-slate-200 shadow-sm text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <PanelLeftOpen className="w-4 h-4" />
          </button>
        )}
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};
